import React, { useState, useEffect } from 'react';
import { Book, FileText, Download, ExternalLink, CheckCircle, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManualsPage = () => {
  const [manuais, setManuais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedManual, setSelectedManual] = useState(null);
  const [manualContent, setManualContent] = useState('');

  useEffect(() => {
    fetchManuais();
  }, []);

  const fetchManuais = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/manuais/lista`);
      const data = await response.json();
      // A API retorna {manuais: [...], total: N}
      setManuais(data.manuais || data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar manuais:', error);
      setLoading(false);
    }
  };

  const viewManual = async (manual) => {
    setSelectedManual(manual);
    try {
      const response = await fetch(`${BACKEND_URL}${manual.endpoint}`);
      const data = await response.json();
      setManualContent(data.content);
    } catch (error) {
      console.error('Erro ao carregar conteúdo:', error);
    }
  };

  const downloadManual = (manual) => {
    const element = document.createElement('a');
    const file = new Blob([manualContent], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = `${manual.id}.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando manuais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  📚 Central de Manuais MonetizeMaster
                </h1>
                <p className="text-gray-600 mt-1">Documentação completa do sistema v2.0</p>
              </div>
            </div>
            <a href="/" className="text-blue-600 hover:text-blue-700 font-medium">
              ← Voltar ao Dashboard
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Sistema Info Card */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">MonetizeMaster v2.0</h2>
              <p className="text-blue-100">
                Sistema Completo de Marketing de Afiliados
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                  9 Plataformas
                </span>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                  4 Redes Sociais
                </span>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                  IA Multilíngue (7+ idiomas)
                </span>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                  Alcance Global (25+ países)
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">100%</div>
              <div className="text-blue-100">Completude</div>
            </div>
          </div>
        </div>

        {/* Documentos Principais */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📊 Documentos Principais</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {manuais && manuais.filter && manuais.filter(m => m.tipo === 'documento' || m.tipo === 'ebook').map((manual) => (
              <div key={manual.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6 border-2 border-blue-200">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <Book className="h-6 w-6 text-blue-600" />
                    {manual.novo && (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                        NOVO
                      </span>
                    )}
                  </div>
                  {manual.versao && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {manual.versao}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{manual.titulo}</h3>
                <p className="text-gray-600 text-sm mb-4">{manual.descricao}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => viewManual(manual)}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Ver
                  </button>
                  <a
                    href={`${BACKEND_URL}${manual.endpoint_html || manual.endpoint}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Módulos */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            📖 {manuais && manuais.filter ? manuais.filter(m => m.tipo === 'modulo').length : 0} Módulos Completos
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {manuais && manuais.filter && manuais.filter(m => m.tipo === 'modulo').map((manual, index) => (
              <div key={manual.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-5 border-l-4 border-blue-500">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    {manual.versao_atualizada && (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                        v2.0
                      </span>
                    )}
                  </div>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{manual.titulo}</h3>
                <p className="text-gray-600 text-xs mb-3">{manual.descricao}</p>
                <button
                  onClick={() => viewManual(manual)}
                  className="w-full bg-blue-50 text-blue-700 px-3 py-2 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  Acessar Módulo
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Guias e Tutoriais */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            📚 Guias e Tutoriais
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {manuais && manuais.filter && manuais.filter(m => m.tipo === 'guia').map((manual) => (
              <div key={manual.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-5 border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-2xl">{manual.titulo.match(/[\u{1F300}-\u{1F9FF}]/u)?.[0] || '📄'}</span>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{manual.titulo.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim()}</h3>
                <p className="text-gray-600 text-xs mb-3">{manual.descricao}</p>
                <button
                  onClick={() => viewManual(manual)}
                  className="w-full bg-green-50 text-green-700 px-3 py-2 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                >
                  Acessar Guia
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Resumos Executivos */}
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            📊 Resumos Executivos
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {manuais && manuais.filter && manuais.filter(m => m.tipo === 'resumo').map((manual) => (
              <div key={manual.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-5 border-l-4 border-purple-500">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-2xl">{manual.titulo.match(/[\u{1F300}-\u{1F9FF}]/u)?.[0] || '📋'}</span>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{manual.titulo.replace(/[\u{1F300}-\u{1F9FF}]/gu, '').trim()}</h3>
                <p className="text-gray-600 text-xs mb-3">{manual.descricao}</p>
                <button
                  onClick={() => viewManual(manual)}
                  className="w-full bg-purple-50 text-purple-700 px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
                >
                  Ver Resumo
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Info adicional */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">ℹ️ Informações Adicionais</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-gray-900 mb-2">🎯 Como Usar Esta Documentação:</h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>✅ Comece pelo <strong>Manual Completo</strong> para visão geral</li>
                <li>✅ Siga os <strong>8 módulos em ordem</strong></li>
                <li>✅ Use o <strong>Resumo Executivo</strong> para referência rápida</li>
                <li>✅ Consulte o <strong>Guia de Marketing Internacional</strong> para expansão global</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-2">📊 Valor do Sistema:</h4>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                <div className="text-3xl font-bold text-green-700 mb-1">
                  R$ 1,5M - 2M
                </div>
                <div className="text-sm text-green-600">
                  Valor estimado de mercado
                </div>
                <div className="mt-3 text-xs text-gray-600">
                  • Custo desenvolvimento: R$ 192k - 411k<br/>
                  • Potencial receita SaaS: R$ 486k/ano<br/>
                  • 960-1.370 horas investidas
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para visualização */}
      {selectedManual && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
              <h3 className="text-xl font-bold text-white">{selectedManual.titulo}</h3>
              <div className="flex gap-2">
                <a
                  href={`${BACKEND_URL}${selectedManual.endpoint_html || selectedManual.endpoint}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <ExternalLink className="h-4 w-4" />
                  Abrir em Nova Aba
                </a>
                <button
                  onClick={() => downloadManual(selectedManual)}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
                <button
                  onClick={() => setSelectedManual(null)}
                  className="bg-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] bg-gray-50">
              <div className="prose prose-sm max-w-none">
                {manualContent ? (
                  <div 
                    className="markdown-content"
                    dangerouslySetInnerHTML={{ 
                      __html: manualContent
                        .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-gray-900 mb-4 mt-6">$1</h1>')
                        .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-gray-800 mb-3 mt-5 border-b pb-2">$1</h2>')
                        .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-gray-700 mb-2 mt-4">$1</h3>')
                        .replace(/^#### (.*$)/gm, '<h4 class="text-base font-semibold text-gray-600 mb-2 mt-3">$1</h4>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        .replace(/`([^`]+)`/g, '<code class="bg-gray-200 px-1 py-0.5 rounded text-sm font-mono">$1</code>')
                        .replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto my-4"><code>$1</code></pre>')
                        .replace(/^- \[ \] (.*$)/gm, '<div class="flex items-start gap-2 my-1"><input type="checkbox" disabled class="mt-1"> <span>$1</span></div>')
                        .replace(/^- \[x\] (.*$)/gm, '<div class="flex items-start gap-2 my-1"><input type="checkbox" checked disabled class="mt-1"> <span class="text-green-700">$1</span></div>')
                        .replace(/^- (.*$)/gm, '<li class="ml-4 my-1">• $1</li>')
                        .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4 my-1"><span class="font-bold">$1.</span> $2</li>')
                        .replace(/\n\n/g, '</p><p class="my-3">')
                        .replace(/---/g, '<hr class="my-6 border-gray-300">')
                    }}
                  />
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Carregando conteúdo...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualsPage;
